import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  message,
  Space,
  Tag,
  Upload,
  Pagination,
  Spin,
  Empty,
} from 'antd';
import {
  DatabaseOutlined,
  ReloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import axios from '../../api';
import theme from '../../theme';
import permissionManager from '../../utils/permissions';
import { buildDifyWebUrl } from '../../config';
import KnowledgeManageCard, { PERMISSION_COLOR_MAP, PERMISSION_TEXT_MAP } from './KnowledgeManageCard';
import './knowledge-list.css';

const KnowledgeManage = () => {
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  });

  const [imageUploadModalVisible, setImageUploadModalVisible] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imageAnalysisLoading, setImageAnalysisLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState('');
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null);

  const canEdit = permissionManager.hasPermission('knowledge', 'edit');
  const canDelete = permissionManager.hasPermission('knowledge', 'delete');
  const canView = permissionManager.hasModulePermission('knowledge');

  const [knowledgeBases, setKnowledgeBases] = useState([]);

  const fetchKnowledgeBases = async (page = 1, pageSize = 12) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/dify-datasets/', {
        params: {
          page,
          limit: pageSize,
          include_all: false,
        },
      });

      setKnowledgeBases(response.data.results || []);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: response.data.count || 0,
      }));
    } catch (error) {
      console.error('获取知识库列表失败:', error);
      message.error('获取知识库列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKnowledgeBases();
  }, []);

  const handlePageChange = (page, pageSize) => {
    fetchKnowledgeBases(page, pageSize);
  };

  const handleAccessExternal = (record) => {
    const url = record.external_url || buildDifyWebUrl(`/datasets/${record.id}/documents`);
    if (!url) {
      message.warning('未配置 Dify 控制台地址（REACT_APP_DIFY_WEB_BASE_URL），无法打开文档管理页');
      return;
    }
    window.open(url, '_blank');
  };

  const handleViewDetail = (record) => {
    Modal.info({
      title: `知识库详情 - ${record.name}`,
      width: 600,
      content: (
        <div>
          <p><strong>ID:</strong> {record.id}</p>
          <p><strong>名称:</strong> {record.name}</p>
          <p><strong>描述:</strong> {record.description || '无描述'}</p>
          <p>
            <strong>权限:</strong>{' '}
            <Tag color={PERMISSION_COLOR_MAP[record.permission]}>
              {PERMISSION_TEXT_MAP[record.permission] || record.permission}
            </Tag>
          </p>
          <p><strong>数据源类型:</strong> {record.data_source_type}</p>
          <p><strong>索引技术:</strong> {record.indexing_technique || '未设置'}</p>
          <p><strong>应用数量:</strong> {record.app_count}</p>
          <p><strong>文档数量:</strong> {record.document_count}</p>
          <p><strong>词数:</strong> {record.word_count}</p>
          <p><strong>创建时间:</strong> {record.created_at ? new Date(record.created_at * 1000).toLocaleString() : '未知'}</p>
          <p><strong>更新时间:</strong> {record.updated_at ? new Date(record.updated_at * 1000).toLocaleString() : '未知'}</p>
        </div>
      ),
    });
  };

  const handleImageUpload = async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过5MB！');
      return false;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post('/api/upload-chat-image/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const backendUrl = typeof window !== 'undefined' ? window.location.origin : '';
        const fullImageUrl = response.data.url.startsWith('http')
          ? response.data.url
          : `${backendUrl}${response.data.url}`;

        const imageData = {
          uid: file.uid,
          name: file.name,
          url: fullImageUrl,
          serverUrl: fullImageUrl,
          filename: response.data.filename,
          status: 'done',
        };
        setUploadedImages((prev) => [...prev, imageData]);
        message.success('图片上传成功');
      } else {
        message.error('图片上传失败');
      }
    } catch (error) {
      console.error('图片上传失败:', error);
      message.error(`图片上传失败: ${error.response?.data?.error || '未知错误'}`);
    }

    return false;
  };

  const handleRemoveImage = (uid) => {
    setUploadedImages((prev) => prev.filter((img) => img.uid !== uid));
  };

  const analyzeImages = async () => {
    if (uploadedImages.length === 0) {
      message.warning('请先上传图片');
      return;
    }

    setImageAnalysisLoading(true);
    try {
      const imageUrls = uploadedImages.map((img) => img.serverUrl);
      const prompt = `请分析这些图片的内容，提取其中的文字信息，并理解图片的整体含义。请按以下格式输出：

1. 图片文字内容：
[提取的纯文字内容]

2. 图片理解：
[对图片内容的整体理解和分析]

3. 关键信息：
[重要的关键信息点]

请确保输出格式清晰，便于后续处理。`;

      const response = await axios.post('/api/analyze-images-with-dify/', {
        images: imageUrls,
        prompt,
      });

      if (response.data.success) {
        const analysis = response.data.analysis || '分析失败';
        setAnalysisResult(analysis);
        message.success('图片分析完成');
      } else {
        message.error(`图片分析失败: ${response.data.error || '未知错误'}`);
      }
    } catch (error) {
      console.error('图片分析失败:', error);
      message.error(`图片分析失败: ${error.response?.data?.error || error.message}`);
    } finally {
      setImageAnalysisLoading(false);
    }
  };

  const generateAndDownloadText = () => {
    if (!analysisResult) {
      message.warning('请先分析图片内容');
      return;
    }

    const textContent = `图片分析结果
生成时间: ${new Date().toLocaleString()}
图片数量: ${uploadedImages.length}

${analysisResult}

图片地址:
${uploadedImages.map((img, index) => `${index + 1}. ${img.url}`).join('\n')}
`;

    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `图片分析结果_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    message.success('文本文件已生成并下载');
  };

  const jumpToDifyUpload = () => {
    if (!selectedKnowledgeBase) {
      message.warning('请先选择知识库');
      return;
    }

    const difyUploadUrl = buildDifyWebUrl(`/datasets/${selectedKnowledgeBase.id}/documents/create`);
    if (!difyUploadUrl) {
      message.warning('未配置 Dify 控制台地址，请先在环境变量中设置 REACT_APP_DIFY_WEB_BASE_URL');
      return;
    }
    window.open(difyUploadUrl, '_blank');

    setImageUploadModalVisible(false);
    setUploadedImages([]);
    setAnalysisResult('');
    setSelectedKnowledgeBase(null);

    message.success('已跳转到Dify上传页面，请手动上传生成的文本文件');
  };

  const openImageUploadModal = (record) => {
    setSelectedKnowledgeBase(record);
    setImageUploadModalVisible(true);
    setUploadedImages([]);
    setAnalysisResult('');
  };

  const closeImageUploadModal = () => {
    setImageUploadModalVisible(false);
    setUploadedImages([]);
    setAnalysisResult('');
    setSelectedKnowledgeBase(null);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: '确定要删除这个知识库吗？',
      content: `将删除「${record.name}」，此操作不可恢复，将删除知识库中的所有文档`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await axios.delete(`/api/dify-delete-dataset/${record.id}/`);
          if (response.data.success) {
            message.success('知识库删除成功');
            fetchKnowledgeBases(pagination.current, pagination.pageSize);
          } else {
            message.error(`知识库删除失败: ${response.data.error || '未知错误'}`);
          }
        } catch (error) {
          console.error('删除知识库失败:', error);
          message.error(`删除知识库失败: ${error.response?.data?.error || error.message}`);
        }
      },
    });
  };

  if (!canView) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#888', fontSize: 20 }}>
        🚫 您没有访问知识库管理的权限
      </div>
    );
  }

  return (
    <div className="kb-manage-page">
      <div className="kb-manage-panel">
        <div className="kb-manage-panel__toolbar">
          <h2 className="kb-manage-panel__title">知识库列表</h2>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchKnowledgeBases(pagination.current, pagination.pageSize)}
              loading={loading}
              style={{ background: theme.BUTTON_BG, color: '#231a17' }}
            >
              刷新
            </Button>
          </Space>
        </div>

        <div className="kb-manage-grid-wrap">
          <Spin spinning={loading}>
            {knowledgeBases.length > 0 ? (
              <div className="kb-manage-grid">
                {knowledgeBases.map((record) => (
                  <KnowledgeManageCard
                    key={record.id}
                    record={record}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onViewDetail={handleViewDetail}
                    onAccess={handleAccessExternal}
                    onUploadImage={openImageUploadModal}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ) : (
              !loading && (
                <div className="kb-manage-empty">
                  <DatabaseOutlined style={{ fontSize: 40, opacity: 0.35 }} />
                  <Empty description="暂无知识库" />
                </div>
              )
            )}
          </Spin>
        </div>

        {pagination.total > 0 && (
          <div className="kb-manage-pagination">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={[12, 24, 48, 96]}
              showTotal={(total) => `共 ${total} 条`}
              onChange={handlePageChange}
            />
          </div>
        )}
      </div>

      <Modal
        title={`上传图片到知识库 - ${selectedKnowledgeBase?.name || ''}`}
        open={imageUploadModalVisible}
        onCancel={closeImageUploadModal}
        footer={[
          <Button key="cancel" onClick={closeImageUploadModal}>
            取消
          </Button>,
          <Button
            key="analyze"
            type="primary"
            loading={imageAnalysisLoading}
            onClick={analyzeImages}
            disabled={uploadedImages.length === 0}
          >
            分析图片
          </Button>,
          <Button
            key="download"
            onClick={generateAndDownloadText}
            disabled={!analysisResult}
          >
            生成文本文件
          </Button>,
          <Button
            key="upload"
            type="primary"
            onClick={jumpToDifyUpload}
            disabled={!analysisResult || !selectedKnowledgeBase}
          >
            跳转到Dify上传
          </Button>,
        ]}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <p>请上传需要分析的图片，系统将使用AI模型提取图片中的文字并理解内容：</p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Upload
            listType="picture-card"
            fileList={uploadedImages}
            beforeUpload={handleImageUpload}
            onRemove={handleRemoveImage}
            accept="image/*"
            multiple
          >
            <div>
              <UploadOutlined />
              <div style={{ marginTop: 8 }}>上传图片</div>
            </div>
          </Upload>
        </div>

        {analysisResult && (
          <div style={{ marginTop: 16 }}>
            <h4>分析结果：</h4>
            <div style={{
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              padding: 12,
              backgroundColor: '#fafafa',
              maxHeight: 300,
              overflow: 'auto',
            }}
            >
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 14 }}>
                {analysisResult}
              </pre>
            </div>
          </div>
        )}

        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6f8fa', borderRadius: 6 }}>
          <h4>操作流程：</h4>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>上传需要分析的图片（支持多张）</li>
            <li>点击「分析图片」使用AI模型提取文字和理解内容</li>
            <li>点击「生成文本文件」下载分析结果</li>
            <li>点击「跳转到Dify上传」将文本文件上传到知识库</li>
          </ol>
        </div>
      </Modal>
    </div>
  );
};

export default KnowledgeManage;
